import { HttpErrorResponse } from '@angular/common/http';
import {Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { FabTableState, filterStateChange, process } from '@fabric/components/table-filter/data-query';
import { catchError, forkJoin, map, Observable, ObservableInput, of } from 'rxjs';
import { ErrorCodes } from '../../constants/ErrorCodes';
import { UserFunctionCode } from '../../constants/UserFunctionCode';
import { Estado, RemMotivoDespacho } from '../../models/RemMotivoDespacho';
import { MotivoService } from '../../services/motivo.service';
import { NotificationService } from '../../services/notification.service';
import { SecurityService } from '../../services/security.service';
import { PopupCancelDialogComponent } from '../../shared-components/popup-cancel-dialog/popup-cancel-dialog.component';
import {DeactivateGuardedComponent} from "../../interfaces/DeactivateGuardedComponent";
import {
  FabDateFilterOperator,
  RemDateFilterComponent
} from '../../shared-components/table-filter/date-filter/date-filter.component';
import {RemNumberFilterComponent} from '../../shared-components/table-filter/number-filter/number-filter.component';
import {
  FabStringFilterOperator,
  RemStringFilterComponent
} from '../../shared-components/table-filter/string-filter/string-filter.component';

const COLUMNS_SCHEMA = [
  { key: 'codMotivo', type: 'text', label: 'Código de Motivo' },
  { key: 'descMotivo', type: 'text', label: 'Descripción del Código' },
  { key: 'fecCreacion', type: 'date', label: 'Fecha de Creación' },
  { key: 'usrCreacion', type: 'text', label: 'Usuario de Creación' },
  { key: 'fecModif', type: 'date', label: 'Fecha de Modificación' },
  { key: 'usrModif', type: 'text', label: 'Usuario de Modificación' },
  { key: 'estado', type: 'boolean', label: 'Estado' },
  { key: 'edited', type: 'boolean', label: '' }
]

class MotivoRow extends RemMotivoDespacho {
  private _originalMotivo: RemMotivoDespacho;
  public add: boolean;
  public editing: boolean = false;

  public set originalMotivo(motivo: RemMotivoDespacho) {
    this._originalMotivo = motivo;
    this.id = motivo.id;
    this.resetEdits();
  }

  public get originalMotivo():RemMotivoDespacho {
    return this._originalMotivo;
  }

  constructor(add: boolean = false, motivo?: RemMotivoDespacho) {
    super();
    this._originalMotivo = motivo ?? new RemMotivoDespacho();
    this.id = motivo?.id ?? 0;
    this.add = add;
    this.editing = add;
    this.resetEdits();
  }

  public resetEdits() {
    this.codMotivo = this.originalMotivo.codMotivo;
    this.descMotivo = this.originalMotivo.descMotivo;
    this.estado = this.originalMotivo.estado;
    this.fecCreacion = this.originalMotivo.fecCreacion;
    this.usrCreacion = this.originalMotivo.usrCreacion;
    this.fecModif = this.originalMotivo.fecModif;
    this.usrModif = this.originalMotivo.usrModif;
  }
}

@Component({
  selector: 'app-reason-code-maintenance',
  templateUrl: './reason-code-maintenance.component.html',
  styleUrls: ['./reason-code-maintenance.component.scss']
})
export class ReasonCodeMaintenanceComponent implements OnInit, DeactivateGuardedComponent  {
  //table render parameters
  columnsToDisplay:string[] = COLUMNS_SCHEMA.map((col) => col.key);
  dataSource: MatTableDataSource<MotivoRow> = new MatTableDataSource<MotivoRow>();
  columnsSchema:any = COLUMNS_SCHEMA;
  //navigation parameters
  operators: FabStringFilterOperator[] = ['contains'];
  dateOperators: FabDateFilterOperator[] = ['eq', 'gte', 'lte'];
  resultsLength = 0;
  pageSizeOptions: number[] = [10,15,20,25];
  defaultPageSize: number = this.pageSizeOptions[0];
  state: FabTableState = {
    skip: 0,
    take: 10,
    filter: [],
    sort: []
  };
  selector: string;

  motivos: MotivoRow[] = [];
  public isLoading = false;
  public today: object = new Date();
  public date = this.today;
  public disableAddButton = false;
  canCreateUpdateUser: boolean = false;

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'codMotivo':
          let splitCode = item.codMotivo?.split('-');
          return splitCode[1]?.padStart(4, '0') ?? '';
        default: // @ts-ignore
          return item[property];
      }
    };
    this.dataSource.sort = sort;
  }
  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) { this.dataSource.paginator = paginator; }

  @ViewChildren('tableFilter') tableFilters: QueryList<RemStringFilterComponent | RemNumberFilterComponent | RemDateFilterComponent>;
  @HostListener('window:beforeunload', ['$event']) defaultConfirmation($event: any) { $event.returnValue(!this.dirtyCheck()); }

  private dirtyCheck(): boolean {
    return !this.motivos.every((row) => {
      return !row.editing;
    })
  }

  constructor(public dialog: MatDialog,
              private formBuilder: FormBuilder,
              readonly motivoService: MotivoService,
              private route: Router,
              private notificationService: NotificationService,
              private securityService: SecurityService,
              private elementRef: ElementRef) {
    // this.dataSource = new MatTableDataSource<any>(TABLE_DATA);
  }

  ngOnInit(): void {
    this.canCreateUpdateUser = this.securityService.can(UserFunctionCode.CREATE_UPDATE_USER_ASSIGN_ROLE);
    this.fetchData();
    this.selector = this.elementRef.nativeElement.tagName.toLowerCase();
  }

  fetchData() {
    this.isLoading = true;
    this.motivoService.getMotivos().subscribe(res => {
      this.motivos = res.map((motivo) => {
        return new MotivoRow(false, motivo);
      });
      this.dataSource = new MatTableDataSource<MotivoRow>(this.motivos);
      this.sortTableByCodMotivo();
      this.processData();
    })
    this.isLoading = false;
  }

  filterChange(event: any) {
    this.state.skip = 0;
    if (this.matPaginator) { this.matPaginator.pageIndex = 0; }
    // @ts-ignore
    this.state.filter = filterStateChange(event, this.state.filter);
    this.processData();
  }

  addRow() {
    let newMotivo = new MotivoRow(true);
    newMotivo.usrCreacion = this.securityService.LOGGED_USER['gmin'];
    newMotivo.estado = true;
    this.motivos.unshift(newMotivo);
    this.processData()
    // this.disableAddButton = true;
  }

  sortTableByCodMotivo() {
    this.motivos.sort(this.sortMotivoRows);
  }

  sortMotivoRows(a: MotivoRow,  b: MotivoRow) {
      return Number(b.codMotivo.split('-')[1]) - Number(a.codMotivo.split('-')[1]);
  }

  saveRows() {
    //TODO check table for duplicates of added rows with active states
    if (this.motivos.filter(element => element.editing).every((row) => this.validateRow(row))) {
      let observables: ObservableInput<any>[] = [];
      let errors: HttpErrorResponse[] = [];
      let saved = 0;
      let motivoCodesCreated: MotivoRow[] = [];
      let error = false;
      this.motivos.filter((item) => item.editing)
        .forEach((motivo) => {
            if (this.isDirtyRow(motivo)) {
              observables.unshift(
                this.motivoService.addUpdateMotivo(motivo, motivo.add)
                .pipe(
                  map((res) => {
                    motivo.originalMotivo = res;
                    motivoCodesCreated.push(motivo);
                    motivo.editing = false;
                    motivo.add = false;
                    saved++;
                    //console.log('In the pipe--> '+motivoCodesCreated);
                  }),
                  catchError((err: HttpErrorResponse) => {
                    errors.push(err);
                    return of(null);
                  })
                )
              );
            } else {
              this.cancelRow(motivo);
            }
          }
        );

      forkJoin(observables).subscribe({
        next: undefined,
        error: undefined,
        complete: () => {
          this.sortTableByCodMotivo();
          motivoCodesCreated.sort(this.sortMotivoRows);
          motivoCodesCreated.reverse();
          let motivoCodesCreatedList = motivoCodesCreated.map(item=>item.codMotivo).join(',');
          if (errors.length == 0) {
            this.notificationService.open(ErrorCodes.SAVED + '\n'+motivoCodesCreatedList, 'success');
          } else {
            const errorCounts = errors.map(err => err.error?.exceptionType ?? "Unknown").reduce((accumulator, value) => {
              // @ts-ignore
              return {...accumulator, [value]: (accumulator[value] || 0) + 1};
            }, {});
            let errorMessage = `${saved} línea(s) guardada(s). `+motivoCodesCreatedList+`\n${errors.length} líena(s) tiene(n) error:`
              .concat(...Object.entries(errorCounts).map(([msg, count]) => `\n${this.notificationService.getErrorByCode(msg)} (${count})`));
            this.notificationService.open(errorMessage, 'error');
          }

        }
      });
    } else {
      // invalid rows error
      this.notificationService.open(ErrorCodes.INVALID_DATA, 'error');
    }
  }

  private validateRow(motivo: MotivoRow) {

    //TODO make the patterns for the fields to check
    //return /^([0-9]{})$/.test(motivo.codMotivo); // && this.motivoService.findActiveMotivo(motivo.codMotivo));
    //this.motivos.filter(element => element.editing).length == 0;
    //console.log(motivo.descMotivo.length);
    return motivo.descMotivo.length > 0 ;
  }

  editRow(element: MotivoRow) {
    if (!element.editing) {
      element.usrModif = this.securityService.LOGGED_USER['gmin'];
      element.editing = true;
    }
  }

  cancelRow(row: MotivoRow) {
    if (row.add) {
      this.motivos = this.motivos.filter((item) => {
        return item !== row;
      })
    }
    row.resetEdits();
    row.editing = false;
    this.processData();
  }

  removeRow(data:any, row:any){
    for (let i = 0; i < data.length; i++) {
      if (data[i].id === row.id) {
        this.dialog
          .open(PopupCancelDialogComponent)
          .afterClosed()
          .subscribe((confirm) => {
            if (confirm) {
              if (row.isAdded === true) {
                this.dataSource.data = this.dataSource.data.filter((u: any) => u.id !== row.id)
              }
              row.isEdit = false;
            }
          });
      }
    }
  }

  processData() {
    this.dataSource.data = process(this.motivos, this.state);
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.dirtyCheck()) {
      return confirm("Usted tiene cambios no guardados. ¿Salir de la página de todos modos?");
    } else {
      return true;
    }
  }

  checkRows(data:any) {
    if(data.descMotivo !== undefined ){
      data.descMotivo = data.descMotivo.trim();
    }

    if ((data.codMotivo !== undefined && data.codMotivo !== '') &&
      (data.descMotivo !== undefined && data.descMotivo !== '') &&
      (data.estado !== undefined)) {
      this.disableAddButton = false;
    }
  }
  public get disableSaveButton() {
    //alert(this.motivos.filter(element => element.editing).length);
    return !this.validateAllRows() ||
      this.motivos.filter(element => element.editing).length == 0;
  }

  public validateAllRows() {
    return this.motivos.filter(element => element.editing).every((row) => this.validateRow(row));
  }

  isDirtyRow(row: MotivoRow): boolean {
    return row.add || row.estado !== row.originalMotivo.estado;
  }
}
